import { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { TinySlider } from '@/components';
import { renderToString } from 'react-dom/server';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import about1 from '@/assets/images/about/01.jpg';
import { getEastCreteImage } from '@/utils/imageUrl';
import '@/assets/css/vendor/tiny-slider.css';

const FeaturedHoliday = ({ sectionData }) => {
  // Use database data if available, otherwise fallback to hardcoded
  const title = sectionData?.title || 'East Crete is Authentic. Unspoiled. Unforgettable.';
  const items = sectionData?.items || [];
  const bgColor = sectionData?.background_color || 'light';
  const bgEffect = sectionData?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';

  // Get multiple images from content.images array or images field, or fallback to single image
  const sectionImages = useMemo(() => {
    // Check for images array in content field
    if (sectionData?.content?.images && Array.isArray(sectionData.content.images) && sectionData.content.images.length > 0) {
      return sectionData.content.images.map(img => getEastCreteImage(img, about1));
    }
    // Check for images field directly
    if (sectionData?.images && Array.isArray(sectionData.images) && sectionData.images.length > 0) {
      return sectionData.images.map(img => getEastCreteImage(img, about1));
    }
    // Fallback to single image field
    if (sectionData?.image && sectionData.image.trim() !== '') {
      return [getEastCreteImage(sectionData.image, about1)];
    }
    // Default fallback
    return [about1];
  }, [sectionData]);
  
  // TinySlider settings for auto-playing carousel
  const sliderSettings = useMemo(
    () => ({
      nested: 'inner',
      autoplay: sectionImages.length > 1, // Only autoplay if multiple images
      autoplayTimeout: 4000, // Change image every 4 seconds
      controls: sectionImages.length > 1, // Only show controls if multiple images
      autoplayButton: false,
      autoplayButtonOutput: false,
      controlsText: [
        renderToString(<BsArrowLeft size={16} />),
        renderToString(<BsArrowRight size={16} />)
      ],
      arrowKeys: true,
      items: 1,
      nav: false
    }),
    [sectionImages.length]
  );

  return <section className={`featured-holiday-section pt-3 pt-lg-4 pb-0 pb-xl-5 mb-4 mb-lg-5 ${bgClass} ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        <Row className="g-4 justify-content-between align-items-center">
          <Col lg={5} className="position-relative">
            {sectionImages.length === 1 ? (
              <img src={sectionImages[0]} className="rounded-3 position-relative" alt={title} />
            ) : (
              <div 
                className="featured-holiday-carousel tiny-slider arrow-round arrow-xs arrow-dark rounded-3 position-relative"
                id="featured-holiday-image-carousel"
              >
                <TinySlider settings={sliderSettings}>
                  {sectionImages.map((image, idx) => (
                    <div 
                      key={`holiday-img-${idx}`}
                      className="w-100"
                    >
                      <img 
                        src={image} 
                        className="rounded-3 position-relative w-100"
                        alt={`${title} - Image ${idx + 1}`}
                        loading="eager"
                        style={{
                          height: 'auto',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.src = about1;
                        }}
                      />
                    </div>
                  ))}
                </TinySlider>
              </div>
            )}
          </Col>

          <Col lg={6}>
            <h2 className="mb-3 mb-lg-5">
              {title.split('East Crete').length > 1 ? (
                <>
                  <span className="text-primary">East Crete</span>
                  {title.split('East Crete')[1]}
                </>
              ) : (
                title
              )}
            </h2>

            {items.length > 0 && (
              <Row className="g-4">
                {items.map((item, index) => {
                  const IconComponent = item.IconComponent;
                  const normalizedColor = (item.icon_color || 'primary').toString().trim().toLowerCase();
                  const iconColorClass = normalizedColor === 'success' ? 'bg-success bg-opacity-10 text-success' :
                                        normalizedColor === 'danger' ? 'bg-danger bg-opacity-10 text-danger' :
                                        normalizedColor === 'warning' ? 'bg-orange bg-opacity-10 text-orange' :
                                        normalizedColor === 'info' ? 'bg-info bg-opacity-10 text-info' :
                                        normalizedColor === 'secondary' ? 'bg-secondary bg-opacity-10 text-secondary' :
                                        'bg-primary bg-opacity-10 text-primary';
                  
                  return (
                    <Col sm={6} key={item.id || index}>
                      {IconComponent && (
                        <div className={`icon-lg ${iconColorClass} rounded-circle flex-centered`}>
                          <IconComponent />
                        </div>
                      )}
                      {item.title && <h5 className="mt-2">{item.title}</h5>}
                      {item.description && (
                        <p
                          className="mb-0 featured-holiday-body-text"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </Col>
                  );
                })}
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </section>;
};
export default FeaturedHoliday;